/**
 * OtpInput - 6-digit OTP input with individual boxes
 */
import React, { useRef, useEffect } from 'react';

function OtpInput({ value, onChange, error, disabled, autoFocus = true }) {
  const inputRefs = useRef([]);
  const otpArray = value.padEnd(6, '').split('').slice(0, 6);
  
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);
  
  const handleChange = (index, digit) => {
    if (!/^\d*$/.test(digit)) return;
    
    // Handle autofill - when multiple digits are entered at once (e.g., from keyboard suggestion)
    if (digit.length > 1) {
      const digits = digit.replace(/\D/g, '').slice(0, 6);
      onChange(digits);
      
      // Focus the last filled input or the last input
      const focusIndex = Math.min(digits.length, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
      return;
    }
    
    const newOtp = otpArray.slice();
    newOtp[index] = digit;
    const newValue = newOtp.join('').slice(0, 6);
    onChange(newValue);
    
    // Move to next input
    if (digit && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };
  
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    
    // Allow arrow navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pastedData);
    
    // Focus last filled input or last input
    const focusIndex = Math.min(pastedData.length, 5);
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };
  
  return (
    <div className="w-full">
      <div className="flex gap-2 sm:gap-3 justify-center">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpArray[index] || ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className={`
              w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold
              rounded-xl border-2 transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-[#004171]/20 focus:border-[#004171]
              disabled:bg-gray-100 disabled:cursor-not-allowed
              ${error 
                ? 'border-red-400 bg-red-50/50' 
                : otpArray[index]
                  ? 'border-[#004171] bg-[#004171]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }
            `}
          />
        ))}
      </div>
      
      {error && (
        <p className="mt-3 text-sm text-red-500 text-center flex items-center justify-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}

export default OtpInput;
